import { faMagnifyingGlass, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, isSameYear, isToday, isYesterday } from "date-fns";
import { cs } from "date-fns/locale";
import { useState } from "react";
import { CertificateIcon } from "../../assets/icons";
import { ASSETS } from "../../config/assets";
import { useAuth } from "../../context/AuthContext";
import { useChat } from "../../context/ChatContext";
import "./MessagesDropdown.css";

const formatConversationTime = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (isToday(date)) return format(date, "HH:mm");

    if (isYesterday(date)) return "Včera";

    return format(date, isSameYear(date, new Date()) ? "d. M." : "d. M. yyyy", {
        locale: cs,
    });
};

const PHOTO_LABEL = "📷 Fotografie";

// Zpráva jen s fotkou nemá text – v náhledu se místo prázdné bubliny
// zobrazí popisek. K textu se přidá, pokud zpráva obsahuje fotky i text.
const DELETED_LABEL = "Zpráva smazána";

const formatPreview = (message, isMine) => {
    const prefix = isMine ? "Já: " : "";

    if (message.is_deleted) return `${prefix}${DELETED_LABEL}`;

    const hasPhotos = (message.attachments || []).length > 0;
    const body = (message.body || "").trim();

    if (!body) return `${prefix}${PHOTO_LABEL}`;

    return hasPhotos ? `${prefix}${body} ${PHOTO_LABEL}` : `${prefix}${body}`;
};

const MessagesDropdown = () => {
    const { user } = useAuth();
    const {
        conversations,
        searchTerm,
        searchResults,
        isSearching,
        searchConversations,
        openChat,
        isOnline,
    } = useChat();
    const [term, setTerm] = useState("");

    const isSearchActive = searchTerm.trim().length > 0;
    const list = isSearchActive ? searchResults || [] : conversations;

    const handleChange = (event) => {
        const value = event.target.value;

        setTerm(value);
        searchConversations(value);
    };

    // Otevře konverzaci a přeskočí na poslední zprávu, kde se hledané slovo vyskytuje
    const handleOpen = (conversation) => {
        openChat(conversation.id, {
            messageId: conversation.match?.message_id ?? null,
            term: searchTerm.trim(),
        });
        setTerm("");
        searchConversations("");
    };

    return (
        <div className="messages_dropdown">
            <div className="messages_dropdown_search input-with-icon">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="icon" />
                <input
                    type="text"
                    className="body_base"
                    value={term}
                    onChange={handleChange}
                    placeholder="Hledejte v konverzacích..."
                    aria-label="Hledat v konverzacích"
                />
                {isSearching && <FontAwesomeIcon icon={faSpinner} className="conversation_search_spinner" />}
            </div>

            <div className="messages_scroll-area">
                {list.length > 0 ? (
                    list.map((conversation) => {
                        const counterpart = conversation.counterpart;
                        const lastMessage = conversation.last_message;
                        const isMine = lastMessage?.user_id === user?.id;
                        const hasUnread = (conversation.unread_count || 0) > 0;

                        return (
                            <div
                                key={conversation.id}
                                className={`button-conversation ${hasUnread ? "unread" : ""}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleOpen(conversation)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        handleOpen(conversation);
                                    }
                                }}
                            >
                                <span className="avatar_wrapper">
                                    <img
                                        className="conversation_avatar"
                                        src={counterpart?.profile_image_url || ASSETS.default_avatar}
                                        alt={counterpart?.first_name || "avatar"}
                                    />
                                    {isOnline(counterpart?.id) && (
                                        <span
                                            className="conversation_online_dot"
                                            title="Online"
                                            aria-label="Online"
                                        />
                                    )}
                                </span>

                                <span className="conversation_right">
                                    <span className="conversation_top">
                                        <p className="name">
                                            {counterpart?.first_name} {counterpart?.last_name}
                                            {counterpart?.is_verified && <CertificateIcon className="certificate_icon--small" />}
                                        </p>
                                        <p className="date">
                                            {formatConversationTime(
                                                conversation.last_message?.created_at ||
                                                conversation.updated_at,
                                            )}
                                        </p>
                                    </span>
                                    <p className="conversation_specs body_smallest">
                                        {counterpart?.specializations_label || "Člen komunity"}
                                    </p>
                                    <span className="conversation_bottom">
                                        <p className="preview">
                                            {lastMessage
                                                ? formatPreview(lastMessage, isMine)
                                                : "Zatím bez zprávy"}
                                        </p>
                                        {hasUnread && (
                                            <span className="unread_count">{conversation.unread_count}</span>
                                        )}
                                    </span>
                                </span>
                            </div>
                        );
                    })
                ) : (
                    <div className="empty-notifications">
                        <p className="body_small strong">
                            {isSearchActive
                                ? "Nic nenalezeno"
                                : "Zatím žádné konverzace"}
                        </p>
                        <p className="body_smallest">
                            {isSearchActive
                                ? "Zkuste hledat jméno, slovo ze zprávy nebo název inzerátu."
                                : "Otevřete profil uživatele a napište mu zprávu."}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessagesDropdown;
